import { RefreshCw } from 'lucide-react'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router'
import { Avatar } from '@/components/tree/Avatar'
import { columns, ingredientMatch, marks, tileRatio, turnable } from '@/lib/modernRecipes'
import { tint } from '@/lib/paper'
import { photoUrl } from '@/lib/photos'
import type { RecipeSummary, Shown } from '@/lib/recipes'
import { cn } from '@/lib/utils'

// How many columns fit: two on a phone, more as the page widens, each at least about 15rem on a big screen.
function useColumnCount() {
  const ref = useRef<HTMLDivElement>(null)
  const [count, setCount] = useState(2)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const fit = () => {
      const w = el.clientWidth
      setCount(w < 640 ? 2 : w < 900 ? 3 : w < 1200 ? 4 : 5)
    }
    fit()
    const watch = new ResizeObserver(fit)
    watch.observe(el)
    return () => watch.disconnect()
  }, [])
  return { ref, count }
}

// The Modern wall: every recipe's dish shown whole in staggered columns, newest near the top. A tile turns over to its
// card (the button in its corner, holding it on a phone, or resting the pointer on it on a computer). What was typed
// in the ingredient box is underlined under each tile that uses it.
export function RecipeWall({ recipes, typed = '' }: { recipes: RecipeSummary[]; typed?: string }) {
  const { ref, count } = useColumnCount()
  const cols = columns(recipes, count, tileRatio)
  return (
    <div ref={ref} className="flex items-start gap-3 sm:gap-4" data-testid="recipe-wall">
      {cols.map((col, c) => (
        <ul key={c} className="flex min-w-0 flex-1 flex-col gap-5 sm:gap-6">
          {col.map((recipe) => (
            <li key={recipe.id}>
              <TurnTile recipe={recipe} typed={typed} index={recipes.indexOf(recipe)} />
            </li>
          ))}
        </ul>
      ))}
    </div>
  )
}

const shownSrc = (shown: Shown) => ({
  src: photoUrl(shown.ref, 'thumb'),
  srcSet: `${photoUrl(shown.ref, 'thumb')} 480w, ${photoUrl(shown.ref, 'display')} ${Math.min(2000, shown.width)}w`,
})

export function TurnTile({ recipe, typed, index }: { recipe: RecipeSummary; typed: string; index: number }) {
  const navigate = useNavigate()
  const [turned, setTurned] = useState(false)
  const canTurn = turnable(recipe)
  const ratio = tileRatio(recipe)
  const onlyCard = !!recipe.tile && recipe.tile.ref === recipe.card?.ref
  // Holding a tile on a phone turns it over instead of opening it; letting go after that doesn't open it either.
  const hold = useRef<{ timer: number; held: boolean } | null>(null)
  const rest = useRef<number | null>(null)
  useEffect(
    () => () => {
      if (hold.current) clearTimeout(hold.current.timer)
      if (rest.current) clearTimeout(rest.current)
    },
    [],
  )
  const match = typed.trim() ? ingredientMatch(recipe, typed).line : null
  const from = recipe.from

  return (
    <div className="group flex flex-col gap-2">
      <div className="relative" style={{ perspective: '1200px' }}>
        <Link
          to={`/recipe/${recipe.id}`}
          viewTransition
          aria-label={recipe.title}
          draggable={false}
          onPointerEnter={(e) => {
            if (!canTurn || e.pointerType !== 'mouse') return
            rest.current = window.setTimeout(() => setTurned(true), 650)
          }}
          onPointerLeave={(e) => {
            if (e.pointerType !== 'mouse') return
            if (rest.current) clearTimeout(rest.current)
            setTurned(false)
          }}
          onPointerDown={(e) => {
            if (!canTurn || e.pointerType === 'mouse') return
            const state = { held: false, timer: 0 }
            state.timer = window.setTimeout(() => {
              state.held = true
              setTurned((t) => !t)
              navigator.vibrate?.(8)
            }, 420)
            hold.current = state
          }}
          onPointerUp={() => hold.current && clearTimeout(hold.current.timer)}
          onPointerCancel={() => hold.current && clearTimeout(hold.current.timer)}
          onContextMenu={(e) => canTurn && e.preventDefault()}
          onClick={(e) => {
            if (hold.current?.held) {
              e.preventDefault()
              hold.current = null
              return
            }
            // A turned tile opens the recipe at its card.
            if (turned) {
              e.preventDefault()
              navigate(`/recipe/${recipe.id}?show=card`, { viewTransition: true })
            }
          }}
          className="block rounded-[1.25rem] outline-none select-none [-webkit-touch-callout:none] focus-visible:ring-[3px] focus-visible:ring-ring/50"
        >
          <div
            className={cn(
              'relative grid transition-transform duration-500 ease-settle [transform-style:preserve-3d] motion-reduce:transition-none',
              turned && '[transform:rotateY(180deg)]',
            )}
            style={{ aspectRatio: `1 / ${ratio}` }}
          >
            <Face>
              {recipe.tile ? (
                <img
                  {...shownSrc(recipe.tile)}
                  sizes="(min-width: 1200px) 18vw, (min-width: 640px) 28vw, 48vw"
                  alt=""
                  loading="lazy"
                  decoding="async"
                  style={{ viewTransitionName: `recipe-${recipe.id}` }}
                  className="size-full object-cover transition-[scale] duration-500 ease-settle group-hover:scale-[1.025]"
                />
              ) : (
                <span
                  className={cn(
                    'grid size-full place-items-center p-4 text-center text-lg font-semibold tracking-tight text-balance',
                    tint(index),
                  )}
                >
                  {recipe.title}
                </span>
              )}
            </Face>
            {recipe.card && canTurn && (
              <Face back>
                <span className="grid size-full place-items-center bg-secondary p-2">
                  <img
                    {...shownSrc(recipe.card)}
                    sizes="(min-width: 1200px) 18vw, (min-width: 640px) 28vw, 48vw"
                    alt=""
                    loading="lazy"
                    decoding="async"
                    className="max-h-full max-w-full rounded-sm object-contain shadow-paper"
                  />
                </span>
              </Face>
            )}
          </div>
          {onlyCard && (
            <span className="absolute top-2.5 left-2.5 rounded-full bg-background/90 px-2.5 py-0.5 text-xs font-semibold text-foreground shadow-sm">
              The card
            </span>
          )}
        </Link>
        {canTurn && (
          <button
            type="button"
            aria-pressed={turned}
            aria-label={turned ? `Show the dish for ${recipe.title}` : `Turn over to the card for ${recipe.title}`}
            onClick={() => setTurned((t) => !t)}
            className="absolute right-2 bottom-2 grid size-9 place-items-center rounded-full bg-background/90 text-foreground shadow-sm backdrop-blur-sm transition-[scale,opacity] outline-none hover:scale-105 focus-visible:ring-[3px] focus-visible:ring-ring/50 pointer-fine:opacity-0 pointer-fine:group-focus-within:opacity-100 pointer-fine:group-hover:opacity-100"
          >
            <RefreshCw
              className={cn('size-4 transition-transform duration-500 ease-settle', turned && 'rotate-180')}
              aria-hidden
            />
          </button>
        )}
      </div>
      <div className="flex flex-col gap-1 px-1">
        <Link
          to={`/recipe/${recipe.id}`}
          viewTransition
          tabIndex={-1}
          className="line-clamp-2 leading-snug font-semibold text-balance"
        >
          {recipe.title}
        </Link>
        <span className="flex min-w-0 items-center gap-1.5 text-sub text-muted-foreground">
          {from ? (
            <>
              <Avatar person={from} className="size-5 text-[0.625rem] shadow-none" />
              <span className="truncate">{from.name}</span>
            </>
          ) : (
            <span className="truncate">Added by {recipe.by}</span>
          )}
        </span>
        {match && (
          <span className="truncate text-sub">
            Uses <Underlined line={match} typed={typed} />
          </span>
        )}
      </div>
    </div>
  )
}

function Face({ back, children }: { back?: boolean; children: ReactNode }) {
  return (
    <span
      className={cn(
        'col-start-1 row-start-1 block size-full overflow-hidden rounded-[1.25rem] shadow-paper [backface-visibility:hidden]',
        back && '[transform:rotateY(180deg)]',
      )}
    >
      {children}
    </span>
  )
}

function Underlined({ line, typed }: { line: string; typed: string }) {
  const found = marks(line, typed)
  if (!found.length) return <>{line}</>
  const parts: ReactNode[] = []
  let at = 0
  for (const [start, end] of found) {
    if (start > at) parts.push(line.slice(at, start))
    parts.push(
      <u key={start} className="decoration-2 underline-offset-[3px]">
        {line.slice(start, end)}
      </u>,
    )
    at = end
  }
  parts.push(line.slice(at))
  return <>{parts}</>
}
